/**
 * Role permissions from DB (GET /roles/permissions) — source of truth for JWT.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Collapse,
  Checkbox,
  Space,
  Typography,
  Button,
  Spin,
  Alert,
  Input,
  message,
} from 'antd';
import { FolderOutlined } from '@ant-design/icons';
import roleService, { Permission } from '@/services/role.service';
import { permissionAliasesOf } from '@/utils/permissionAliases';

const { Text } = Typography;

export interface DbPermissionEditorProps {
  roleId: string;
  roleName: string;
  isSystemRole?: boolean;
  onSaved?: () => void;
}

export const DbPermissionEditor: React.FC<DbPermissionEditorProps> = ({
  roleId,
  roleName,
  isSystemRole,
  onSaved,
}) => {
  const [allPerms, setAllPerms] = useState<Permission[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, assigned] = await Promise.all([
        roleService.getAllPermissions({ is_active: true }),
        roleService.getRolePermissions(roleId),
      ]);
      const perms = Array.isArray(all) ? all : [];
      // Legacy alias codes (e.g. sales.*) are hidden; a grant on one shows as its canonical twin,
      // and saving drops the legacy row.
      const idByCode = new Map(perms.map((p) => [p.code, p.id]));
      const canonicalId = (p: Permission) => idByCode.get(permissionAliasesOf(p.code)[0]) ?? p.id;
      setAllPerms(perms.filter((p) => permissionAliasesOf(p.code)[0] === p.code));
      setSelectedIds(new Set((assigned || []).map(canonicalId)));
    } catch (e: any) {
      const d = e?.response?.data;
      const msg =
        (typeof d?.message === 'string' && d.message) ||
        (typeof d?.error === 'string' && d.error) ||
        e?.message ||
        'Failed to load permissions';
      message.error(msg);
      setAllPerms([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    load();
  }, [load]);

  const byModule = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map<string, Permission[]>();
    for (const p of allPerms) {
      if (q) {
        const hay = `${p.code} ${p.name} ${p.module} ${p.description || ''}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      const m = p.module || 'Other';
      if (!map.has(m)) map.set(m, []);
      map.get(m)!.push(p);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [allPerms, search]);

  const toggle = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleModule = (perms: Permission[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const p of perms) {
        if (checked) next.add(p.id);
        else next.delete(p.id);
      }
      return next;
    });
  };

  const moduleCheckedState = (perms: Permission[]) => {
    const n = perms.filter((p) => selectedIds.has(p.id)).length;
    if (n === 0) return { checked: false, indeterminate: false };
    if (n === perms.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  const handleSave = async () => {
    if (isSystemRole) return;
    setSaving(true);
    try {
      await roleService.assignPermissions(roleId, Array.from(selectedIds));
      message.success('Permissions saved');
      onSaved?.();
    } catch (e: any) {
      const d = e?.response?.data;
      const msg =
        (typeof d?.message === 'string' && d.message) ||
        (typeof d?.error === 'string' && d.error) ||
        e?.message ||
        'Failed to save permissions';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      {isSystemRole && (
        <Alert
          type="warning"
          showIcon
          message="System / template role — clone this role to customize permissions."
          style={{ marginBottom: 16 }}
        />
      )}
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Text type="secondary">
          {roleName} — {selectedIds.size} permission(s) selected
        </Text>
        <Input.Search
          placeholder="Filter by code, name, module"
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
        <Collapse
          defaultActiveKey={byModule.slice(0, 6).map(([m]) => m)}
          items={byModule.map(([module, perms]) => {
            const st = moduleCheckedState(perms);
            return {
              key: module,
              label: (
                <Space>
                  <FolderOutlined />
                  <Text strong>{module}</Text>
                  <Text type="secondary">({perms.length})</Text>
                </Space>
              ),
              extra: (
                <Checkbox
                  checked={st.checked}
                  indeterminate={st.indeterminate}
                  disabled={isSystemRole}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => toggleModule(perms, e.target.checked)}
                >
                  All
                </Checkbox>
              ),
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {perms.map((p) => (
                    <Checkbox
                      key={p.id}
                      checked={selectedIds.has(p.id)}
                      disabled={isSystemRole}
                      onChange={(e) => toggle(p.id, e.target.checked)}
                    >
                      <Text code>{p.code}</Text> — {p.name}
                    </Checkbox>
                  ))}
                </div>
              ),
            };
          })}
        />
        {!isSystemRole && (
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save permissions
          </Button>
        )}
      </Space>
    </div>
  );
};

export default DbPermissionEditor;
